/** The launch band (plan §1 "Launch band"): six nodes in the booking spine's slot — a
 *  sticky band on the page background with a bottom hairline — Mapping, Import, Launch
 *  policy, Access, Tag categories, Guest app. Each node is a button (keys `1`–`6`, bound by
 *  the page) that scrolls to its panel block and focuses it.
 *
 *  - Datum lines are the stored tour's (the identity strip's numbers, per node).
 *  - Node state is a shape AND words, from the readiness rules (`readiness.ts`, the same
 *    list as the panel's checks; the draft laid over the tour in edit mode): a navy disc
 *    with a check and "Ready", an amber ring with "!" and "2 to check" — and, for a node
 *    no rule looks at (Access, Tag categories, Guest app), an empty outlined ring and
 *    "No checks": it never claims a readiness nobody checked.
 *  - In edit mode a node carries its unsaved-change count (the navy pill) and, amber, how
 *    many of them the next import will undo — below 1100 px of content a dot and a sync
 *    disc (`MarkBadges compact`), as on bookings.
 *
 *  A `<nav>` of an ordered list of buttons. */
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { alpha, Box, ButtonBase, Typography, type Theme } from '@mui/material';
import { forwardRef, useMemo } from 'react';

import type { TourDetail } from '@/api/tours';
import { MarkBadges, marksLabel, type StageMarks } from '@/features/bookings/record/views/SpineNav';
import { IMPORT_STATE } from '@/features/import-runs/importRunFormat';
import { SCROLLER_PADDING_PX } from '@/features/records/page/recordPage';
import { formatRelativeTime } from '@/utils/format';

import { checksByNode, READINESS_NODES, RULED_NODES, type ReadinessCheck, type ReadinessNode } from '../readiness';

type NodeState = 'fine' | 'attention' | 'unchecked';

export const BAND_TITLES: Record<ReadinessNode, string> = {
  mapping: 'Mapping',
  import: 'Import',
  policy: 'Launch policy',
  access: 'Access',
  'tag-categories': 'Tag categories',
  'guest-app': 'Guest app',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Each node's datum line, from the stored tour. */
export function bandDatums(detail: TourDetail): Record<ReadinessNode, string> {
  const parked = detail.additional_custom_ids.filter((a) => !a.use_for_matching).length;
  const aliases = detail.additional_custom_ids.length;
  const runs = detail.import_summary.runs ?? [];
  const failed = runs.filter((r) => r.state === IMPORT_STATE.FAILURE || r.state === IMPORT_STATE.PARTIAL_SUCCESS).length;
  const source = detail.import_summary.sources[0];
  const def = detail.operator_default_launch_policy;
  const own = detail.launch_policy;
  const ownCats = detail.tag_categories_own;
  const inherited = detail.use_operator_defaults ? detail.tag_categories_default : [];
  const effective = [...ownCats, ...inherited.filter((r) => !ownCats.some((o) => o.category?.id === r.category?.id))];
  const names = (rows: typeof effective) => rows.map((r) => r.category?.name || r.category?.code).filter(Boolean).join(', ') || '—';
  const onOff = (v: boolean) => (v ? 'on' : 'off');
  return {
    mapping: `${detail.custom_id ? '1 primary' : 'no primary'} · ${plural(aliases, 'alias', 'aliases')} / ${parked} parked`,
    import: detail.import_summary.is_import_bound
      ? [
          source ? source.name || source.code : 'Imported',
          runs[0] ? formatRelativeTime(runs[0].created_ts) : 'no runs',
          failed ? `${failed} with errors` : 'no errors',
        ].join(' · ')
      : 'Not imported',
    policy: own
      ? `${own.name ?? `#${own.id}`} · own${def && def.id !== own.id ? ` (default ${def.name ?? `#${def.id}`})` : ''}`
      : def
        ? `${def.name ?? `#${def.id}`} · operator default`
        : 'No policy',
    access: `${plural(detail.atag.length, 'access tag', 'access tags')} · ${plural(detail.tags.length, 'system tag', 'system tags')}`,
    'tag-categories': `${ownCats.length} own · ${inherited.length} inherited · check-in ${names(effective.filter((r) => r.checkin))} · boarding ${names(effective.filter((r) => r.boarding))}`,
    'guest-app': `Self-reg ${onOff(detail.allow_selfreg)} · chat ${onOff(detail.has_group_chat && !detail.exclude_from_chat)} · My Trips ${onOff(detail.mytrips_display)}`,
  };
}

function nodeSx(state: NodeState) {
  return (t: Theme) => ({
    width: 18,
    height: 18,
    borderRadius: '50%',
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    boxSizing: 'border-box' as const,
    ...(state === 'attention'
      ? { border: `2px solid ${t.palette.warning.main}`, color: t.palette.warning.dark, bgcolor: alpha(t.palette.warning.main, 0.12) }
      : state === 'fine'
        ? { bgcolor: t.palette.primary.main, color: t.palette.primary.contrastText }
        : { border: `2px solid ${alpha(t.palette.primary.main, 0.35)}`, bgcolor: t.palette.background.paper }),
  });
}

const STATE_WORDS = (state: NodeState, n: number) =>
  state === 'attention' ? `${n} to check` : state === 'fine' ? 'Ready' : 'No checks';

export interface LaunchBandProps {
  detail: TourDetail;
  checks: ReadinessCheck[];
  editing: boolean;
  marks: Partial<Record<ReadinessNode, StageMarks>>;
  managedBy: string | null;
  onSelect: (node: ReadinessNode) => void;
  /** Below 1100 px of content: the marks shrink to a dot and the sync disc. */
  compactMarks?: boolean;
}

export const LaunchBand = forwardRef<HTMLDivElement, LaunchBandProps>(function LaunchBand(
  { detail, checks, editing, marks, managedBy, onSelect, compactMarks = false },
  ref,
) {
  const datums = useMemo(() => bandDatums(detail), [detail]);
  const byNode = checksByNode(checks);
  return (
    <Box
      ref={ref}
      data-launch-band
      sx={{
        position: 'sticky',
        top: -SCROLLER_PADDING_PX,
        zIndex: 3,
        bgcolor: 'background.default',
        mx: -3,
        px: 3,
        pt: 1.25,
        pb: 1,
        mb: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box component="nav" aria-label="Launch readiness">
        <Box
          component="ol"
          sx={{
            listStyle: 'none',
            m: -0.75,
            p: 0.75,
            display: 'grid',
            gridTemplateColumns: `repeat(${READINESS_NODES.length}, minmax(120px, 1fr))`,
            columnGap: 1,
            overflowX: 'auto',
          }}
        >
          {READINESS_NODES.map((node, i) => {
            const found = byNode[node];
            // Import is checked only once there is a run to look at.
            const ruled = RULED_NODES.has(node) && (node !== 'import' || (detail.import_summary.runs ?? []).length > 0);
            const state: NodeState = found.length > 0 ? 'attention' : ruled ? 'fine' : 'unchecked';
            const attention = state === 'attention';
            const m = editing ? marks[node] : undefined;
            const status = STATE_WORDS(state, found.length);
            const extra = marksLabel(m, managedBy);
            return (
              <Box component="li" key={node} sx={{ minWidth: 0 }}>
                <ButtonBase
                  onClick={() => onSelect(node)}
                  data-band-node={node}
                  data-state={state}
                  aria-label={[`${i + 1}. ${BAND_TITLES[node]}: ${datums[node]}`, status, extra || null].filter(Boolean).join('. ')}
                  title={found.map((c) => c.title).join(' · ') || undefined}
                  sx={{
                    width: '100%',
                    display: 'block',
                    textAlign: 'left',
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                    '&:hover .band-title': { color: 'primary.main', textDecoration: 'underline', textUnderlineOffset: 3 },
                    '&.Mui-focusVisible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Box aria-hidden sx={nodeSx(state)}>
                      {state === 'attention' ? (
                        <PriorityHighIcon sx={{ fontSize: 12 }} />
                      ) : state === 'fine' ? (
                        <CheckIcon sx={{ fontSize: 12 }} />
                      ) : null}
                    </Box>
                    <Typography className="band-title" variant="body2" component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {BAND_TITLES[node]}
                    </Typography>
                    <MarkBadges marks={m} managedBy={managedBy} compact={compactMarks} />
                  </Box>
                  <Typography
                    variant="caption"
                    component="span"
                    sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {datums[node]}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="span"
                    data-band-status
                    sx={{ display: 'block', color: attention ? 'warning.dark' : 'text.secondary', fontWeight: attention ? 600 : 400 }}
                  >
                    {status}
                  </Typography>
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
});
